import React, { useCallback, useRef } from 'react';
import http from 'utils/http';
import Spacer from '../../Spacer/Spacer';
import AutocompleteAdapter from './AutocompleteAdapter';
import { AdapterProps } from '../types';
import { useQuery } from '@tanstack/react-query';
import Skeleton from '@mui/material/Skeleton';
import { Stack } from '@mui/material';
import debounce from 'lodash/debounce';
import { Pagination } from '../../../../modules/@common/crud/types';

export type ServerAutocompleteAdapterProps =
  AdapterProps['server-autocomplete'] &
    Omit<AdapterProps['autocomplete'], 'options'>;

type ServerResource = {
  id: number;
  name: string;
};

type ServerResponse = ServerResource[] | Pagination<ServerResource>;

function isPaginationResponse(
  response: ServerResponse,
): response is Pagination<ServerResource> {
  return (
    response.hasOwnProperty('data') &&
    Array.isArray((response as Pagination<ServerResource>).data)
  );
}

const ServerAutocompleteAdapter = ({
  optionsUrl,
  ...props
}: ServerAutocompleteAdapterProps) => {
  const inputValueRef = useRef('');
  const {
    data = [],
    isLoading,
    isFetching,
    refetch,
  } = useQuery({
    queryKey: [
      optionsUrl,
      'server-autocomplete-adapter-options',
      props.params || {},
    ],
    queryFn: async () => {
      let items = await http
        .get<any, ServerResponse>(optionsUrl, {
          params: {
            ...(props.params || {}),
            query: inputValueRef.current,
          },
        })
        .then((response) => {
          if (isPaginationResponse(response)) {
            return response.data;
          }

          return response;
        });

      return items.map((item) => ({
        value: item.id,
        label: item.name,
      }));
    },
  });

  const debouncedRefetch = useCallback(
    debounce(() => {
      void refetch();
    }, 250),
    [refetch],
  );

  if (isLoading) {
    return (
      <Stack direction="column" flex="1">
        <Skeleton width="100%" height={16} variant="rectangular" />
        <Spacer size={1} />
        <Skeleton width="100%" height={54} variant="rectangular" />
      </Stack>
    );
  }

  return (
    <AutocompleteAdapter
      {...props}
      options={data}
      isLoading={isFetching}
      clearOnBlur={false}
      onInputChange={(event, value, reason) => {
        inputValueRef.current = value;
        debouncedRefetch();
      }}
    />
  );
};

export default ServerAutocompleteAdapter;
